import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { Box, Flex } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { SpotlightCard, SpotlightPulse } from '@atlaskit/onboarding';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { fg } from '@confluence/feature-gating';

const i18n = defineMessages({
	AIStreamingSpotlightTitle: {
		id: 'onboarding-ai-first-create.ai-streaming.spotlight.title',
		defaultMessage: 'You’re in control',
		description: 'Title for AI streaming spotlight',
	},
	AIStreamingSpotlightDescription: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.description',
		defaultMessage: 'Undo, redo or view changes from recent edits.',
		description: 'Description text for AI streaming spotlight',
	},
	AIStreamingSpotlightCTA: {
		id: 'onboarding-ai-first-create.ai-streamin.spotlight.cta',
		defaultMessage: 'OK',
		description: 'CTA button text for AI streaming spotlight',
	},
});

const spotlightStyles = css({
	position: 'absolute',
	marginInline: token('space.150'),
	bottom: '180px',
	right: '65px',
	'& h4': {
		font: `${token('font.heading.medium')} !important`,
	},
});

export type AIStreamingSpotlightProps = {
	children: React.ReactNode;
	aiIsStreaming: boolean;
	primaryActionsLength: number;
};

export const AIStreamingSpotlight = ({
	children,
	aiIsStreaming,
	primaryActionsLength,
}: AIStreamingSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const prevIsAIStreamingRef = useRef(aiIsStreaming);
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const { setOnboardingState } = useSetOnboardingState();

	const { data, loading, error } = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { hasSeenAIStreamingSpotlight } = deserializeState(data);

	useEffect(() => {
		if (aiIsStreaming) {
			prevIsAIStreamingRef.current = aiIsStreaming;
		}
	}, [aiIsStreaming]);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIStreamingSpotlight,
			value: 'true',
		});

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'AIStreamingSpotlight',
				attributes: {
					source: 'onbparding-ai-first-create',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const { useChoreographer } = useChoreographerAssets();

	const aiStreamingSpotlightChoreographer = useChoreographer('AIStreamingSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleSpotlightInteraction = useCallback(
		(action: string, actionSubject: string, actionSubjectIdSuffix?: string) => {
			setIsSpotlightActive(false);

			aiStreamingSpotlightChoreographer.stopMessage();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: `AIStreamingSpotlight${actionSubjectIdSuffix ?? ''}`,
					source: 'onbparding-ai-first-create',
				},
			}).fire();
		},
		[createAnalyticsEvent, aiStreamingSpotlightChoreographer],
	);

	useEffect(() => {
		if (
			!loading &&
			!error &&
			!hasSeenAIStreamingSpotlight &&
			!aiIsStreaming &&
			prevIsAIStreamingRef.current &&
			primaryActionsLength !== 0 &&
			fg('aifc_onboarding_killswitch')
		) {
			aiStreamingSpotlightChoreographer.startMessage();
		}
	}, [
		loading,
		error,
		hasSeenAIStreamingSpotlight,
		aiIsStreaming,
		primaryActionsLength,
		aiStreamingSpotlightChoreographer,
	]);

	if (!isSpotlightActive) {
		return <>{children}</>;
	}

	return (
		<div data-testid="ai-streaming-spotlight">
			<div css={spotlightStyles}>
				<SpotlightCard
					width={275}
					heading={formatMessage(i18n.AIStreamingSpotlightTitle)}
					actions={[
						{
							text: formatMessage(i18n.AIStreamingSpotlightCTA),
							onClick: () => handleSpotlightInteraction('clicked', 'button', 'CTA'),
						},
					]}
				>
					{formatMessage(i18n.AIStreamingSpotlightDescription)}
				</SpotlightCard>
			</div>
			<Box>
				<SpotlightPulse radius={3} pulse={isSpotlightActive}>
					{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<Box onClick={() => handleSpotlightInteraction('clicked', 'button', 'Target')}>
						<Flex direction="column" gap="space.100" justifyContent="center" alignItems="center">
							{children}
						</Flex>
					</Box>
				</SpotlightPulse>
			</Box>
		</div>
	);
};
